import { isChainPointerRow } from '@papan/types'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, Folder } from 'lucide-react'

import { useTranslation } from '@papan/i18n'
import { FileIcon, fileIconProps, miniappIconProps } from '@papan/core-ui/ui'
import { filesApi } from '@papan/files/client'
import type { FileListItem } from '@papan/files/client'
import { fileKeys, identityGuardedRead } from '@papan/files/client/queries'
import { formatRelativeDate, formatSize } from '../formatters'
import { useDebounced } from './hooks'
import { SearchScopeControl, type SearchScope } from './SearchScopeControl'

/**
 * The reusable search core: fires the recursive-name query for `q` under
 * `rootId` and renders the result rows (icon · name · location · size ·
 * modified). Query source, chrome, and navigation are the host's job — the
 * standalone page ({@link SearchResultsView}) drives it from the URL, the
 * in-folder mode in DriveView drives it from local state and passes a
 * `rootId`. ONE renderer/fetcher so there's no parallel search list to keep
 * in sync.
 */
export function SearchResults({
  q,
  rootId = null,
  scope,
  onOpen,
}: {
  q: string
  /** null = tenant-wide; a folder id scopes the search to that subtree. */
  rootId?: string | null
  /** A chip in the count line that narrows or widens the search; the host flips `rootId`. */
  scope?: SearchScope
  onOpen: (f: FileListItem) => void
}) {
  const { t } = useTranslation('files')
  const enabled = q.length >= 2
  const queryClient = useQueryClient()
  const searchKey = fileKeys.search(rootId, q)
  const search = useQuery({
    queryKey: searchKey,
    // #1210: identity-scoped key — never read as, or answered by, another identity
    queryFn: () => identityGuardedRead(queryClient, searchKey, () => filesApi.searchFiles(q, { rootId, limit: 100 })),
    enabled,
    staleTime: 30_000,
  })

  const files = search.data?.files ?? []

  if (!enabled) return <div className="text-sm themed-text-muted py-6">{t('driveHome.searchPage.typeToSearch')}</div>
  if (search.isLoading) return <div className="text-sm themed-text-muted py-6">{t('driveHome.loading')}</div>
  if (files.length === 0) return <div className="text-sm themed-text-muted py-6">{t('driveHome.searchPage.noResults', { query: q })}</div>

  return (
    <>
      <div className="text-xs themed-text-muted mb-3 flex items-center gap-2 flex-wrap">
        <span>
          {t('driveHome.searchPage.count', { count: files.length, query: q })}
          {search.data?.truncated && <span> · {t('driveHome.searchPage.truncated')}</span>}
        </span>
        {scope && <SearchScopeControl scope={scope} />}
      </div>
      <div className="ws-home-list">
        {files.map(f => (
          <button
            key={f.id}
            onClick={() => onOpen(f)}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:themed-bg-tertiary transition-colors"
            data-testid="search-result-row"
          >
            <FileIcon {...fileIconProps(f)} size={16} {...miniappIconProps(f)} />
            <span className="flex-1 min-w-0 text-sm themed-text truncate">{f.name}</span>
            {f.parentName && (
              <span className="hidden md:flex items-center gap-1 text-xs themed-text-muted max-w-[12rem]">
                <Folder size={11} className="shrink-0" />
                <span className="truncate">{f.parentName}</span>
              </span>
            )}
            <span className="hidden sm:block text-xs themed-text-muted w-16 text-right shrink-0">
              {f.isFolder ? '—' : formatSize(f.size)}
            </span>
            <span className="text-xs themed-text-muted w-24 text-right shrink-0">
              {formatRelativeDate(f.updatedAt)}
            </span>
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * Dedicated search results page (`/search?q=…`) — the Google-Drive-style
 * full view behind the Home quick-suggestions dropdown. The query lives in
 * the URL so results are shareable/back-button-friendly; the local input
 * refines it (debounced, replace-navigation to avoid history spam). Renders
 * the shared {@link SearchResults} core, tenant-wide (no `rootId`).
 */
export function SearchResultsView({ urlPrefix }: { urlPrefix: string }) {
  const { t } = useTranslation('files')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const q = (searchParams.get('q') || '').trim()

  const [input, setInput] = useState(q)
  // URL is the source of truth (back/forward, Home handoff) — sync it in.
  useEffect(() => { setInput(q) }, [q])
  const debounced = useDebounced(input.trim(), 300)
  useEffect(() => {
    if (debounced !== q) setSearchParams(debounced ? { q: debounced } : {}, { replace: true })
  }, [debounced]) // eslint-disable-line react-hooks/exhaustive-deps

  const open = (f: FileListItem) => {
    // A chain is REVEALED beside its siblings, never entered from search: search rows don't carry
    // `chainNeedsApproval`, and the row's own open runs the D17 gate (I114).
    if (f.isFolder && !isChainPointerRow(f)) navigate(`${urlPrefix}/folder/${f.id}`)
    else navigate(f.parentId ? `${urlPrefix}/folder/${f.parentId}?reveal=${f.id}` : `${urlPrefix}/folder?reveal=${f.id}`)
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto w-full px-8 py-8">
        <div className="flex items-center gap-2 mb-5">
          <Search size={18} className="text-indigo-400" />
          <h1 className="text-xl font-semibold themed-text">{t('driveHome.searchPage.title')}</h1>
        </div>

        <div className="relative mb-6 max-w-xl">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
          <input
            name="drive-search-page"
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('driveHome.search.placeholder')}
            data-testid="drive-search-page-input"
            className="w-full themed-input ws-drive-pill rounded-xl pl-9 pr-4 py-2.5 text-sm"
          />
        </div>

        <SearchResults q={q} onOpen={open} />
      </div>
    </div>
  )
}
